"use strict";

// General modules
var $ = require('jquery');
var _ = require('underscore');
var Backbone = require('backbone');
var Promise = require('promise-polyfill');
var ajax = require('ajax');

var MiniTimer = require('MiniTimer');
var SafeStorage = require('storage/SafeStorage');
var TemplateCache = require('TemplateCache');
var Registry = require('registry');
var getQueryParameters = require('queryparams');

// App modules
var Result = require('./Result');
var ResultStore = require('./ResultStore');
var SidebarView = require('./views/SidebarView');
var SortOrderView = require('./views/SortOrderView');
var ResultView = require('./views/ResultView');
var FilterInfoView = require('./views/FilterInfoView');

var FormModel = require('./models/FormModel');
var ReservaEngineClient = require('./ReservaEngineClient');
var TraminoClientsMap = require('../TraminoClientsMap');

// Included modules
var ReservaDaysModel = require('./ReservaDaysModel');


var ReservaApp = Backbone.Model.extend({

    /* Schlüssel, die beim Reset ignoriert werden sollen */
    resetIgnore: [
        'item', 'form', 'client', 'selectedTags', 'config',
        'tags_groups', 'isTouchDevice', 'autoHideWidgets',
        'queryToken',
    ],

    defaults: function() {
        return {
            /* Parameters */
            item: null,
            client: null,
            config: {}, /* Geht alles an die Registry */
            form: null,
            queryToken: null,
            tags_groups: [], /* Wird an die API durchgereicht */
            autoHideWidgets: true,

            /* State */
            selectedTags: [],
            searchQuery: "", /* Aktuelle Suchanfrage */
            sortBy: 'auto', /* auto, stars, ratings, price */
            sortArrayIndex: '0', /* 0, 1, 2, 3 */
            tagsCount: {}, /* Mapping of tag_id => Count */
            tab: "list", /* list, map */
            isTouchDevice: false, /* Sind wir auf Mobile? */
            favoritesOnly: false, /* Nur Favoriten anzeigen? */
            filterInfos: [],
            lastSearch: null,
            minPrice: null,
            maxPrice: null,
        };
    },

    //
    // Lifecycle Methods
    //

    /**
     * Grundsätzlichen Zustand der Anwendung intialisieren.
     */
    initialize: function() {
        /* Achtung: Der Code muss unabhängig von der Seite sein, etwa wenn nur die Widgets gebraucht werden */
        this.queryParameters = getQueryParameters();
        this.resetWorld();

        let tags_groups = this.get("tags_groups");
        if ( !Array.isArray(tags_groups) ) {
            if ( tags_groups === "" ) {
                this.set("tags_groups", []);
            } else {
                this.set("tags_groups", tags_groups.split(',').filter(t => !!t));
            }
        }

        // Initialize registry values, used for global static configuration
        _.each(this.get("config"), function(value, key) {
            Registry.set(key, value);
        });

        // How many items are rendered by batch
        this.renderBatchSize = 9;

        // Model for the input form (mostly used by the dropdowns)
        this.formModel = new FormModel();

        // Limit days in the calendar when a package was selected (not in resetWorld as this never changes)
        this.daysModel = new ReservaDaysModel();

        /* Wir führen einmal ein history.pushState aus bei der Suche, um die Suchparameter aus der URL zu putzen.
          Dies behebt ein Problem was auftritt, wenn die User von Detaileinträgen zurück gehen
          und dadurch wieder Standardwerte aus der URL geladen werden. */
        this.hasPushedState = false;

        /* Widget default status */
        this.datepicker_initialized = false;
        this.peopleselect_initialized = false;

        _.bindAll(this,
            "processResults",
            "promiseError",
            "renderResults",
            "renderMoreResults",
            "scrollHandler",
            "search",
            "updateFavButton",
            "updateMapMarkers",
            "viewAnalytics"
        );

        require('./WidgetsMixin')(this);

        this.viewAnalyticsThrottled = _.throttle(this.viewAnalytics, 100);

        /* If it looks like a duck, assume it's one */
        var isSomethingMobile = navigator.userAgent.match(/iPad|iPhone|Android|Windows Phone/i) !== null;
        if ( isSomethingMobile ) {
            console.log("[ReservaApp] User-Agent looks like mobile");
            this.set("isTouchDevice", true);
            this.formModel.set("isTouchDevice", true);
        } else {
            /* Touch device detection */
            var handler = function() {
                console.log("[ReservaApp] Detected touch event, going into touch device mode");
                this.set("isTouchDevice", true);
                this.formModel.set("isTouchDevice", true);
                $(document).off('touchstart', handler);
            }.bind(this);
            $(document).on('touchstart', handler);
        }

        /* Template Helfer-Funktionen */
        TemplateCache.helper("tagSelected", function(tag_id) {
            tag_id = parseInt(tag_id, 10);
            return this.get("selectedTags").indexOf(tag_id) !== -1;
        }.bind(this));

        TemplateCache.helper("hasTag", function(result, tag_id) {
            let found = false;
            for (var i = 0; i < result.classes.length; i++) {
                if ( !result.classes[i].tags ) continue;

                if ( result.classes[i].tags.indexOf(tag_id) !== -1 ) {
                    found = true;
                    break;
                }
            }
            return found;
        }.bind(this));

        TemplateCache.helper("tagName", function(tag_id) {
            return this.tagNames[tag_id];
        }.bind(this));
    },

    /**
     * Zustand der Anwendung zurücksetzen. Notwendig bevor neue Suchanfragen gestellt werden können.
     */
    resetWorld: function() {
        this.resetting = true;

        // Reset renderer state
        this.lastRenderIndex = 0;

        // Collection of all retrieved results
        this.resultStore = new ResultStore();

        // Mapping of client_id => ResultView
        _(this.resultViews).invoke('remove');
        this.resultViews = {};

        // List of tag groups (sidebar data source)
        this.tagGroups = [];

        // Mapping of tag_id => [ class_id, ... ]
        this.tagMapping = {};

        // Mapping of class => [ tag_id, ...]
        this.classTagMapping = {};

        // Mapping of tag => name
        this.tagNames = {};

        // Mapping of tag => name (for fast lookup)
        this.importantTags = {};

        // ReservaEngineClient object       
        this.result = null;

        // TraminoClientsMap object
        this.map = null;

        // Lock for renderMoreResults
        this.renderMoreResultsLock = false;

        // List of filtered results
        this.searchResults = [];

        // Reset Model state
        var defaults = this.defaults();
        for(var i = 0, key; key = this.resetIgnore[i]; ++i) {
            delete defaults[key];
        }
        this.set(defaults);

        // Previous search parameters
        this.prevSearchDate = null;
        this.prevSearchPeople = null;

        // Reset form
        var searchQueryInput = $('input[name="searchQuery"]');
        searchQueryInput.val('');
        searchQueryInput.on('keydown', function(e) {
            if ( e.keyCode == 13 ) {
                e.preventDefault();
            }
        });
        this.searchQueryInput = searchQueryInput;

        this.resetting = false;
    },

    /**
     * App starten
     *
     * @param run_options {Object} Zusätzliche Optionen für die Widgets
     */
    run: function(run_options) {
        if ( window.ReservaAppExecuted ) {
            throw new Error("ReservaApp was run twice on a page. This is not supported and creates strange problems.");
        }
        window.ReservaAppExecuted = true;

        run_options = run_options || {};

        /* fixOversize suport */
        if ( $.fixoversize ) {
            this.on("change:tags", function() {
                console.log("[Reserva] Calling fixoversize update (change:tags)");
                $.fixoversize("update");    
            })
        }

        /* Formular finden */
        if ( !this.attributes.form || !this.attributes.form.length ) {
            this.attributes.form = $('.ibe-container form');
        }

        /* Per URL vorgegebenen Formularparameter */
        if ( typeof this.queryParameters.from !== "undefined" ) {
            this.queryParameters.select_mode = "exact";
        }

        console.log("[ReservaApp] Setting query parameters to form:", this.queryParameters);
        this.formModel.set(this.queryParameters);
        this.formModel.bindToForm(this.attributes.form);

        if ( typeof window.Raven !== "undefined" ) {
            Raven.setTagsContext({ app: "engine" });
        }

        /* Werbung aus dem DOM sammeln */
        this.banners = $('.banner-inner');

        /* Bei Änderung der Suchfilter */
        this.on('change:selectedTags change:minPrice change:maxPrice', function(e) {
            this.filterResults();
            this.renderResults();
            this.updateMapMarkers();
            this.scrollUp();
        });

        /* Gastgeber-Filter */
        var searchQueryInput = $('input[name="searchQuery"]');
        searchQueryInput.on('keyup', _.debounce(function() {
            var searchQuery = searchQueryInput.val().replace(/^\s+|\s+$/g, "");
            this.set("searchQuery", searchQuery);
        }.bind(this), 300));

        this.on('change:searchQuery', function(e) {
            // Stichwortsuche
            this.filterResults();
            this.renderResults();
        }.bind(this));

        /* Scroll-Handler fürs Nach-Rendern von Ergebnissen */
        $(window).on('scroll', _.throttle(this.scrollHandler, 300));

        /* Beim Resize des Fensters Ergebnis-Liste neu rendern */
        $(window).on('resize', _.debounce(this.renderResults, 1000));

        /* Beim Submit des Formulars die Suche per AJAX ausführen */
        this.attributes.form.on('submit', function(e) {
            e.preventDefault();
            this.hideDatepicker();
            this.hidePeopleSelect();
            this.search();
        }.bind(this));

        /* Suche zurücksetzen */
        $('.ibe-container .reset-engine').on('click', function(e) {
            e.preventDefault();
            localStorage.clear(); sessionStorage.clear();
            location.reload(); // YOLO
        }.bind(this));

        /* Suchen bei Auswahl eines Datums */
        this.on("DatePicker.dateSelected", function() {
            this.formModel.set("select_mode", "exact"); /* Fixes a really obscure bug */
            this.hideDatepicker();
            this.search();
        }.bind(this));

        /* Views vorbereiten */
        this.sidebarView = new SidebarView({
            el: document.getElementById('engine-sidebar'),
            model: this,
        });
        this.sortOrderView = new SortOrderView({
            el: document.getElementById('sort_order'),
            model: this,
        });
        this.filterInfoView = new FilterInfoView({
            el: document.getElementById('search-filter-info'),
            model: this,
        });
        this.$list = $('#results-list');

        /* Rest der UI rendern */
        this.sortOrderView.render();

        /* Is Storage broken? */
        if ( SafeStorage.local.hasCookiesDisabled ) {
            //Raven.captureMessage("CookiesDisabled");
        }
        if ( SafeStorage.local.looksLikePrivateMode ) {
            //Raven.captureMessage("PrivateMode");
        }

        /* Standard-Widgets initialisieren */
        if ( !this.datepicker_initialized ) {
            this.initializeDatepicker(run_options.datepicker || {
                'form': this.attributes.form,
                'datepicker_display': $('#reserva_datepicker_display'),
                /* Hack: Use the window jQuery which has jquery-ui included */
                'datepicker_container': window.$('#reserva_datepicker'),
            });
        }

        if ( this.formModel.get("from") ) {
            this.datepicker.datepicker.datepicker("setDate", this.formModel.get("from"));
        }

        if ( !this.peopleselect_initialized ) {
            this.initializePeopleSelect(run_options.peopleselect || {
                'people_display': $('#reserva_people_display'),
                'people_container': $('#reserva-people-select'),
            });
        }

        /* Haben wir ein Item? */
        this.on('change:item', this.loadItemDays);
        if ( this.get("item") ) {
            this.loadItemDays().then(this.search);
        } else {
            /* Sobald alles bereit ist die Suche durchführen */
            this.search();
        }

        /* Button für die Favoriten */
        if ( window.TraminoFavoriten ) {
            TraminoFavoriten.on('change:items', function(e) {
                console.log("[ReservaApp] Neue Favoriten", e.attributes.items);
                this.updateFavButton();
                if ( this.get("favoritesOnly") ) {
                    this.filterResults();
                    this.renderResults();
                }
            }.bind(this));
            this.updateFavButton();
        }

        /* Tabs für Liste und Karte initialisieren */
        var buttons = $('.result-tabs-buttons span');
        var tabs = $('.result-tabs .tab');
        var self = this;
        buttons.on('click', function(e) {
            var $this = $(this);
            //var prevTab = self.get("tab");
            var tab = $this.data('href');
            console.log("Switch to", tab);
            self.set("tab", tab);

            // Buttons aktivieren
            buttons.removeClass('active');
            $this.addClass('active');

            var $target = $('#tab-' + tab);
            tabs.removeClass('active');
            $target.addClass('active');

            // Wir sparen Zeit, wenn wir Google Maps nur einmal initialisieren und dann wiederverwerten
            if ( tab === "geo" && !self.map ) {
                // Karte erzeugen, aber noch keine Marker definieren.
                $('#partner-map').html("Einen Moment bitte, Karte wird geladen...");
                self.map = new TraminoClientsMap({
                    target: 'partner-map',
                    autofit: true,
                    onClick: function(e, marker, betrieb) {
                        var view = self.resultViews[betrieb.id].clone();
                        view.render();
                        self.map.displayInfo(marker, view.el);
                    },
                });
                self.map.init().then(function() {
                    // Marker vom Server laden und einfügen 
                    if ( !self.result ) {
                        // This happens when a user clicks on the tab when the results are not ready yet. Idiot.
                        console.log("Could not initialize map: Result not ready yet");
                        return;
                    }
                    self.updateMapMarkers();
                }).catch(self.promiseError);
            }

            /* Sortierung bei der Karte ausblenden */
            if ( tab === "geo" ) {
                $('.tabs .right').hide();
            } else {
                $('.tabs .right').show();
            }
        });

        /* Favoriten Filter */
        $('.fav-group span').on('click', function(e) {
            var current = $(this);
            var href = current.data().href;

            if ( href === "all" ) {
                self.set("favoritesOnly", false);
            }
            if ( href === "favs" ) {
                self.set("favoritesOnly", true);
            }
        });

        this.on('change:favoritesOnly', function(e) {
            var state = e.attributes.favoritesOnly;
            var fav_group = $('.fav-group');
            if ( state ) {
                fav_group.find('span[data-href="all"]').removeClass('active');
                fav_group.find('span[data-href="favs"]').addClass('active');
            } else {
                fav_group.find('span[data-href="all"]').addClass('active');
                fav_group.find('span[data-href="favs"]').removeClass('active');
            }

            self.filterResults();
            self.renderResults();
            self.updateMapMarkers();

        }.bind(this));
    },

    /**
     * Favoriten-Button aktualisieren
     */
    updateFavButton: function(e) {
        if ( !window.TraminoFavoriten ) return;

        var count = 0;
        var favs = TraminoFavoriten.getItemsMap();
        this.resultStore.each(function(result) { // n = 0 - 800
            if ( !result.get("visible") ) return;
            if ( typeof favs["clients-" + result.get("id")] !== "undefined" ) {
                count++;
            }
        });
        $('.fav-group span[data-href="favs"]').text(
            count === 0
            ? __("favorites")
            : (count === 1
                ? __("one_favorite")
                : count + " " + __("favorites")
               )
        );
    },

    /**
     * Marker auf der Karte aktualisieren
     */
    updateMapMarkers: function() {
        if ( !this.map ) return;
        this.map.clearMarkers();
        var clients = this.resultStore.getVisible().map(function(result) {
            return {
                id: result.id,
                name: result.get("name"),
                lat: result.get("lat"),
                lng: result.get("lng"),
            };
        });
        this.map.addMarkers(clients);
    },

    /**
     * Nicht anderweitig abgefangene Promise-Fehler behandeln
     */
    promiseError: function(e) {
        var extra = {};
        console.log("promiseError", e);

        if ( typeof e.readyState !== "undefined" ) {
            // Looks like a jqXHR object
            extra.status = e.status;
            extra.statusText = e.statusText;
            extra.responseText = e.responseText;
            e = new Error("AJAX call failed");
            console.log("promiseError-ajax", e, extra);
        }

        if ( typeof window.Raven !== "undefined" ) {
            Raven.captureException(e, { extra: extra });
        }

        if ( this.$list && this.$list.length > 0 ) {
            this.$list.html(TemplateCache.render("result-error", {
                error: e.statusText || e.message || e.error || e.toString(),
                showError: window.Tramino.user && window.Tramino.user > 0,
            }));
        } else {
            // Can happen on the miniengine
        }
    },

    //
    // Model methods
    //

    loadTags: function () {
        var timer = MiniTimer("Load Tags");

        var handler = function(response) {
            timer(); timer = MiniTimer("Parse Tags");

            this.tagGroups = [];
            this.tagMapping = {};
            this.classTagMapping = {};
            this.tagNames = {};

            var currentGroupName = null;
            var currentTags = [];

            for (var i = 0, result; result = response.tags_info[i]; ++i) {
                // result = { order, group, id, tag, [classes] }
                if ( !currentGroupName ) currentGroupName = result.group;
                if ( currentGroupName != result.group ) {
                    this.tagGroups.push({
                        index: i,
                        name: currentGroupName,
                        tags: currentTags,
                    });
                    currentGroupName = result.group;
                    currentTags = [];
                }

                currentTags.push({
                    id: result.id,
                    tag: result.tag,
                    count: null, /* null = Unknown count */
                });
                this.tagNames[result.id] = result.tag;

                var classes = result.classes.split(',');

                /* Wir erzeugen hier ein Mapping von tag_id => Array von Klassen.
                   Dies ermöglicht es uns später, schnell anhand der ausgewählten Tags die Ergebnisse zu filtern */
                for(var j = 0, class_id; class_id = classes[j]; ++j) {
                    if ( typeof this.tagMapping[result.id] === "undefined" ) {
                        this.tagMapping[result.id] = [];
                    }
                    this.tagMapping[result.id].push(parseInt(class_id, 10)); // result.id = tag.id

                    if ( typeof this.classTagMapping[class_id] === "undefined" ) {
                        this.classTagMapping[class_id] = [];
                    }
                    this.classTagMapping[class_id].push(parseInt(result.id, 10)); // result.id = tag.id
                }
            }

            this.tagGroups.push({
                name: currentGroupName,
                tags: currentTags,
            });

            this.trigger("change:tags");

            timer();

        }.bind(this);

        let lang = window.Tramino.lang;
        let tags_groups = this.get("tags_groups") ? this.get("tags_groups").join(",") : "";
        let cache_key = `engine.tags.${lang}.${tags_groups}`;
        let cache = SafeStorage.session.get(cache_key);

        if ( cache ) {
            console.log("[Engine] Loading filter tags from SafeStorage.session cache");
            return Promise.resolve(JSON.parse(cache)).then(handler).catch(this.promiseError);
        } else {
            console.log("[Engine] Loading filter tags from API");
            let params = {
                lang: lang,
            };
            if ( tags_groups ) {
                params.groups = tags_groups;
            }

            return ajax('/api/engine/tags/', {params: params}).then(function(response) {
                SafeStorage.session.set(cache_key, JSON.stringify(response), 3600 * 1000);
                return response;
            }).then(handler).catch(this.promiseError);
        }
    },

    /**
     * Lock the search engine. Prevents many funny and strange bugs which happens
     * when multiple searches are running concurrently.
     */
    lockSearch: function() {
        if ( this.searchLocked ) {
            console.log("[Engine] Search is locked, ignored request");
            Raven.captureException(new Error("SearchAlreadyLocked"));
            return false;
        }
        this.searchLocked = true;
        $('.search input[type=submit]').attr('disabled', true).addClass('locked');

        this.searchQueryInput.hide();
        return true;
    },

    /**
     * Release the search engine again
     */
    releaseSearch: function(ignore) {
        if ( !this.searchLocked && !ignore ) {
            console.log("[Engine] Search is not locked, ignored request");
            Raven.captureException(new Error("UnlockAlreadyUnlockedSearch"));
            return false;
        } 
        this.searchLocked = false;
        $('.search input[type=submit]').attr('disabled', false).removeClass('locked');

        this.searchQueryInput.show();
        return true;
    },

    /**
     * Handler bei der ersten Suche registrieren
     */
    registerFirstSearchHandler: function() {
        /* Bei Formular-Änderungen den Such-Button pulsieren lassen */

        this.allowPulse = true;
        this.formModel.on('change', (e) => {
            if ( !this.allowPulse ) return;
            if ( typeof e.changed.to !== "undefined" || typeof e.changed.from !== "undefined" ) return;

            $('.ibe-container .search input[type=submit]')
                .effect('highlight', 2000)
                .effect('highlight', 2000);

            this.allowPulse = false;

            setTimeout(() => {
                this.allowPulse = true;
            }, 4000);
        });
    },

    /**
     * Suche durchführen. Setzt die Anwendung zurück, führt eine Abfrage aus und lädt weitere Ergebnisse nach.
     */
    search: function() {
        if ( !this.lockSearch() ) return;

        if ( this.get("lastSearch") === null ) {
            this.registerFirstSearchHandler();
        }

        var timer = MiniTimer("Execute Search");
        var formData = this.formModel.getForm();
        formData.lang = window.Tramino.lang;
        formData.querytoken = this.get("queryToken");

        var errors = this.formModel.validate(formData);
        if ( errors && errors.length > 0 ) {
            this.releaseSearch();
            this.$list.html(TemplateCache.render("result-error-validate", {
                errors: errors,
            }));
            this.$list.find('.btn-reset').on('click', function() {
                localStorage.clear();
                sessionStorage.clear();
                location.reload(); // YOLO
            }.bind(this));
            return;
        }

        /* Wenn der User anfängt ein Datum auszuwählen und dann direkt auf Suchen klickt. */
        if ( this.formModel.get("to") === null && this.formModel.get("select_mode") !== "none" ) {
            this.datepicker.runAwayScreaming();
            this.showDatepicker();
            return;
        }

        this.$list.html(TemplateCache.render("loader-results"));

        if ( !this.hasPushedState && typeof history.pushState !== "undefined" && !this.queryParameters.expected_clients ) {
            try {
                history.pushState(null, null, location.href.split('?')[0]);
            } catch ( e ) { /* Ignore this problem */ }
            this.hasPushedState = true;
        }

        /* Reset the application state */
        this.resetWorld();
        this.set("lastSearch", formData);

        this.updatePeopleDisplay();
        this.prevSearchDate = this.datepickerButton.text();
        this.prevSearchPeople = this.peopleButton.text();

        this.loadTags().then(function() {

            ReservaEngineClient.search(formData).then(function(result) {
                this.resultStore = new ResultStore();
                this.result = result;

                if ( (Tramino.user === "10" || Tramino.user === "1") && ! navigator.userAgent.match(/MSIE/)  ) {
                    var benchmark = result.response.refs.benchmark;
                    var ApiBench = require('apibench');
                    var server = location.hostname.indexOf(".dev") === -1 ? 'https://live.tramino.de' : 'http://live.tramino.dev';
                    ajax(benchmark.replace('{live}', server)).then(function(response) {
                        ApiBench.init();
                        ApiBench.render(response);
                    }.bind(this)).catch(this.promiseError);
                }

                // Erste Ergebnisse rendern, dann gleich mehr anfordern
                this.processResults(result.getInitialResults());

                /* Falls wir Tags aus der URL erhalten haben, diese anwenden */
                var requestedTags = this.queryParameters.tags;
                if ( requestedTags ) {
                    this.set("selectedTags", _.map(requestedTags.split(','), function(x) { return parseInt(x, 10); }));
                }

                // Gleich die restlichen Ergebnisse anfordern
                if ( result.more_results ) {
                    var timer2 = MiniTimer("Load rest of results");
                    result.loadMore().then(function(results) {
                        this.processResults(results);
                        timer2();

                        this.releaseSearch();
                    }.bind(this)).catch(this.promiseError);
                } else {
                    this.releaseSearch();
                }

                timer();

            }.bind(this), function(e) {
                // e = jqXHR object
                this.$list.html(TemplateCache.render("result-error", {
                    error: e.statusText || e.message || e.toString(),
                    showError: window.Tramino.user && window.Tramino.user > 0,
                }));
                if ( typeof window.Raven !== "undefined" ) {
                    var error = new Error(e.statusText || e.message || e.toString());
                    Raven.captureException(error, {
                        extra: {
                            formData: formData,
                        },
                    });
                }

                /* Die User sollen weiterhin suchen dürfen */
                this.releaseSearch();

            }.bind(this)).catch(this.promiseError);

        }.bind(this)).catch(this.promiseError);

    },

    /**
     * Einkommende Ergebnisse verarbeiten:
     * - In den resultStore packen
     * - Views erzeugen
     * - Tags filtern (visible status)
     * - Sortieren
     * - Anzeigen
     */
    processResults: function(results) {
        var timer = MiniTimer("processResults");
        var first_batch = this.resultStore.length === 0;
        for (var i = 0, result; result = results[i]; ++i) { // n = 1-20
            // Standard-State setzen
            result.visible = false;
            result.viewSent = false;
            result.name_lower = result.name.toLowerCase();
            result.name_search = (result.name_search || "").toLowerCase();

            // Sortierparameter auftrennen
            var sort = result.sort ? result.sort : [0,0,0,0];
            result.sort_auto    = parseFloat(sort[0]);
            result.sort_stars   = parseFloat(sort[1]);
            result.sort_ratings = parseFloat(sort[2]);
            result.sort_price   = parseFloat(sort[3]);

            _.each(result.classes, function(item) {
                var sort = item.sort ? item.sort : [0,0,0,0];
                item.sort_auto    = parseFloat(sort[0]);
                item.sort_stars   = parseFloat(sort[1]);
                item.sort_ratings = parseFloat(sort[2]);
                item.sort_price   = parseFloat(sort[3]);
                item.name_lower   = item.name.toLowerCase();
                item.tags         = this.classTagMapping[item.id];
            }.bind(this));

            var model = new Result(result);
            var view = new ResultView({
                model: model,
            });
            view.app = this;

            this.resultStore.add(model);
            this.resultViews[result.id] = view;
        }

        /* @todo Remove this until the bug is fixed */
        if ( window.Tramino.root && this.queryParameters.expected_clients && !first_batch ) {
            let expected_clients = this.queryParameters.expected_clients.split(',').map(c => parseInt(c, 10));
            let missing_clients = [];
            for (var i = 0; i < expected_clients.length; i++) {
                let client_id = expected_clients[i];
                if ( !this.resultViews[client_id] ) {
                    missing_clients.push(client_id);
                }
            }

            if ( missing_clients.length > 0 ) {
                $('#search-filter-info').after(`<div class="search-filter-info"><span class='tag'>[Debug] Folgende Clients wurden erwartet, aber fehlen im Ergebnis: ${missing_clients.join(", ")}</span></div>`);
            }
        }

        this.sortResults();
        this.filterResults();
        if ( first_batch ) {
            this.renderResults();
            this.updateMapMarkers();
        }
        timer("added " + results.length + " results");
    },

    /**
     * Ergebnisse anhand der aktuellen Bedingungen filtern:
     * - Nach Tags eingrenzen
     * - Nur Favoriten/ausgewählte Kategorien anzeigen
     * - Preis Min/Max
     * - Stichwortsuche
     *
     * Die Suche nach Namen erfolgt gesondert bei renderResultsSearch wegen der besonderen Darstellung.
     */
    filterResults: function() {
        var app = this;
        var selectedTags = this.get("selectedTags");
        var classTagMapping = this.classTagMapping;
        var tagMapping = this.tagMapping;
        var timer = MiniTimer("filterResults");
        var filter_infos = [
            { text: this.prevSearchDate, closeable: false, onclick: () => { this.hidePeopleSelect(); this.showDatepicker(); } },
            { text: this.prevSearchPeople, closeable: false, onclick: () => { this.hideDatepicker(); this.showPeopleSelect(); } },
        ];

        if ( this.resultStore.length === 0 ) {
            /* Wenn es nix zu sortieren gibt, brauchen wir uns keine Arbeit machen */
            return;
        }

        /* Am Anfang ist erstmal alles sichtbar */
        this.resultStore.each(function(result) { // n = 0 - 800
            var classes = result.get('classes');
            result.attributes.visible = true;
            _.each(classes, function(klass) { // n = 0 - ~30
                klass.visible = true;
            });
        });

        /* Danach filtern wir die Tags */
        if ( selectedTags.length > 0 ) {
            var tagNames = [];
            var tagIds = {};
            for (var i = 0; i < selectedTags.length; ++i ) {
                let name = this.tagNames[selectedTags[i]];
                tagNames.push(name);
                tagIds[name] = selectedTags[i];
            }
            _.each(tagNames, function(t) {
                filter_infos.push({
                    text: t,
                    onclick: function() {
                        app.toggleTag(tagIds[t]);
                    },
                });
            });

            // Sichtbarkeit berechnen
            let count = 0;
            this.resultStore.each(function(result) { // n = 0 - 800
                // result = Backbone.Model
                var classes = result.get('classes');
                var visible_classes = 0;

                /* Für jede Klasse prüfen wir, ob sie sichtbar ist */
                _.each(classes, function(klass) { // n = 0 - ~30
                    var visible = true;

                    for (var i = 0, tag_id; tag_id = selectedTags[i]; ++i) { // n = 0 - 4
                        var classes_with_this_tag = tagMapping[tag_id];

                        if ( !classes_with_this_tag ) {
                            /* This is okay because the interest-filter might send us tags we don't know about. We'll just ignore them for now. */
                            console.error("tagMapping is missing tag #" + tag_id);
                            return;
                        }
                        
                        if ( classes_with_this_tag.indexOf(parseInt(klass.id, 10)) === -1 ) {
                            visible = false;
                        }
                    }

                    if ( visible ) visible_classes++;
                    klass.visible = visible;
                });

                if ( visible_classes > 0 ) {
                    count++;
                } else {
                    result.attributes.visible = false;
                }
            }.bind(this));

            if ( count === 0 ) {
                this.set("selectedTags", []);
            }
        } else {
            // Es werden alle Ergebnisse und Klassen angezeigt
            this.resultStore.each(function(result) { // n = 0 - 800
                result.set("visible", true);
                _.each(result.get('classes'), function(klass) {
                    klass.visible = true;
                });
            });
        }


        // Filterung nach Favoriten
        if ( this.get("favoritesOnly") ) {
            console.log("Filtering by favorites");
            var favs = TraminoFavoriten.getItemsMap();
            this.resultStore.getVisible().each(function(result) { // n = 0 - 800
                if ( typeof favs["clients-" + result.get("id")] === "undefined" ) {
                    result.attributes.visible = false;
                }
            });

            filter_infos.push({
                text: "nur Favoriten",
                onclick: function() {
                    this.set("favoritesOnly", false);
                }.bind(this)
            });
        }
        this.updateFavButton();


        // Filter nach Preis
        var minPrice = this.get("minPrice");
        var maxPrice = this.get("maxPrice");
        if ( minPrice !== null || maxPrice !== null ) {
            if ( minPrice !== null && maxPrice !== null && minPrice > maxPrice ) {
                this.set({
                    minPrice: maxPrice,
                    maxPrice: minPrice,
                });
                return;
            }

            this.resultStore.getVisible().each(function(result) { // n = 0 - 800
                var classes = result.get('classes');
                var visible_classes = 0;

                _.each(classes, function(klass) { // n = 0 - ~30
                    var visible = true;

                    if ( minPrice !== null && klass.price < minPrice ) visible = false;
                    if ( maxPrice !== null && klass.price > maxPrice ) visible = false;

                    if ( visible ) visible_classes++;
                    klass.visible = visible;
                });

                if ( visible_classes === 0 ) {
                    result.attributes.visible = false;
                }
            }.bind(this));

            if ( minPrice !== null ) {
                filter_infos.push({
                    text: "Mindestpreis " + minPrice + " €",
                    onclick: function() {
                        this.set("minPrice", null);
                    }.bind(this)
                });
            }
            if ( maxPrice !== null ) {
                filter_infos.push({
                    text: "Maximalpreis " + maxPrice + " €",
                    onclick: function() {
                        this.set("maxPrice", null);
                    }.bind(this)
                });
            }
        }


        // Stichwortsuche
        this.searchResults = [];
        var query = this.get("searchQuery").toLowerCase();
        if ( query ) {
            let useCategorySearch = Registry.get("Engine.CategorySearch", false);

            /* Wir sammeln alle Ergebnisse welche auf die Beschreibung passen und packen diese in eine extra Liste.
               Deren Sichtbarkeit wird false, da diese dann über die extra Liste gerendet werden. */
            this.resultStore.getVisible().each(function(result) { // n = 0 - 800
                console.log(result.get("name_search"), result.get("name_search").indexOf(query) !== -1);
                if ( result.get("name_lower").indexOf(query) !== -1 || result.get("name_search").indexOf(query) !== -1 ) {
                    result.attributes.visible = false;
                    this.searchResults.push(result);
                }
                else if ( useCategorySearch ) {
                    /* Vielleicht finden wir es anhand der Kategorie */
                    let found = false;
                    _.each(result.get("classes"), (klass) => {
                        if ( !found && klass.name_lower.indexOf(query) !== -1 ) {
                            result.attributes.visible = false;
                            this.searchResults.push(result);
                            found = true;
                        }
                    });
                }

            }.bind(this));

            filter_infos.push({
                text: '"' + this.get("searchQuery") + '"',
                onclick: function() {
                    this.set("searchQuery", "");
                    $('input[name="searchQuery"]').val('');
                }.bind(this)
            });
        }


        // Tags zählen
        var tagCounts = {};
        _.each(this.tagGroups, function(group) { // n = ~4
            _.each(group.tags, function(tag) { // n = ~8
                tagCounts[tag.id] = [];
            });
        });

        var count = 0;
        this.resultStore.getVisible().each(function(result) { // n = 0 - 100
            _.each(result.get('classes'), function(klass) {
                if ( !klass.visible ) return;

                var tags_with_this_classes = classTagMapping[klass.id];
                _.each(tags_with_this_classes, function(tag_id) {
                    // Nur Tags zählen, die wir auch in der Sidebar anzeigen
                    if ( tagCounts.hasOwnProperty(tag_id) ) {
                        tagCounts[tag_id].push(result.id);
                    }
                });
            });
            count++;
        });

        _.each(this.tagGroups, function(group) { // n = ~4
            _.each(group.tags, function(tag) { // n = ~8
                if ( tagCounts[tag.id] ) {
                    tag.count = _.unique(tagCounts[tag.id]).length;
                } else {
                    tag.count = 0;
                }
            });
            group.tags = _(group.tags).sortBy(function(tag) { return -tag.count; });
        }.bind(this));

        this.set("filterInfos", filter_infos);

        this.trigger("change:tags");
        timer(count + " results are visible");
    },

    /**
     * Die Suchergebnisse rendern. Setzt den State auf 0, löscht die aktuelle Liste und erzeugt diese komplett neu.
     */
    renderResults: function() {
        var timer = MiniTimer("renderResults");

        /* For performance reasons, we keep all result nodes around
           and just detach and re-attach them to the DOM when necesssary */
        _.each(this.resultViews, function(view) {
            view.hide();
        });
        this.lastRenderIndex = 0;

        var searchQuery = this.get("searchQuery");
        if ( searchQuery ) {
            this.$list.html("");
            this.$list.append("<div class='results-divider'>Suchergebnisse</div>");

            var views = this.resultViews;
            _.each(this.searchResults, function(result) {
                var view = views[result.id];
                if ( typeof view === "undefined" ) {
                    throw new Error("BUGCHECK: Missing resultView of result #" + result.id);
                }
                view.show(this.$list);
            }.bind(this));

            this.$list.append("<div class='clear'></div>");
            this.$list.append("<div class='results-divider'>Weitere Gastgeber</div>");
        }

        if ( !this.resetting ) {
            this.renderMoreResults();
        }
        timer();
    },

    /**
     * Zusätzliche Ergebnisse rendern
     */
    renderMoreResults: function() {
        if ( this.renderMoreResultsLock ) return;
        this.renderMoreResultsLock = true;

        let timer = MiniTimer("renderMoreResults");
        let boxesPerRow = 0;

        let visible_models = this.resultStore.getVisible();
        let models = visible_models.slice(this.lastRenderIndex, this.lastRenderIndex + this.renderBatchSize);

        let noSearchResults = this.searchResults.length === 0;
        if ( models.length === 0 && this.lastRenderIndex === 0 || (this.get("searchQuery") && noSearchResults) ) {
            console.log("Rendering results 404");
            this.$list.html(TemplateCache.render('no-results', {
                favoritesOnly: this.get("favoritesOnly"),
                searchQuery: this.get("searchQuery"),
            }));
        }
        else if ( models.length > 0 ) {

            console.log("[renderMoreResults] Found " + models.length + " results to render");

            // Sicherstellen, dass die Box leer ist falls keine Ergebnisse gerendert wurden
            if ( this.$list.find('.result-box').length === 0 ) {
                console.log("[renderMoreResults] Emptying this.$list");
                this.$list.html("");
            }

            let views = this.resultViews;
            _.each(models, function(result, i) {
                if ( !result.attributes.visible ) return;
                let view = views[result.id];
                if ( typeof view === "undefined" ) {
                    throw new Error("BUGCHECK: Missing resultView of result #" + result.id);
                }

                view.show(this.$list);
                this.lastRenderIndex++;

                if ( !boxesPerRow ) {
                    /* Wir können das erst nach der ersten Box berechnen */
                    let listWidth = this.$list.width();
                    let boxWidth = this.$list.find('.result-box').first().width();
                    boxesPerRow = Math.ceil(listWidth / boxWidth);
                    if ( boxesPerRow > 3 ) boxesPerRow = 3;
                }

                // Werbung einblenden
                let favoritesOnly = this.get("favoritesOnly");
                // console.log("AdCheckGeneral", visible_models.length > 6, !favoritesOnly, boxesPerRow > 0);
                if ( visible_models._wrapped.length > 6 && !favoritesOnly && boxesPerRow > 0 ) {

                    if ( this.banners[0] && this.lastRenderIndex === (boxesPerRow * 1) ) {
                        // console.log("Render banner row 1", boxesPerRow, this.lastRenderIndex);
                        $(this.banners[0]).clone().appendTo(this.$list).show();
                    }

                    if ( this.banners[1] && this.lastRenderIndex === (boxesPerRow * 3) ) {
                        // console.log("Render banner row 2", boxesPerRow, this.lastRenderIndex);
                        $(this.banners[1]).clone().appendTo(this.$list).show();
                    }

                    if ( this.banners[2] && this.lastRenderIndex === (boxesPerRow * 5) ) {
                        // console.log("Render banner row 3", boxesPerRow, this.lastRenderIndex);
                        $(this.banners[2]).clone().appendTo(this.$list).show();
                    }
                }


                if ( this.lastRenderIndex <= boxesPerRow && !result.viewSent ) {
                    window.TraminoAnalytics.send(result.get("id"), "view");
                    result.viewSent = true;
                }

            }.bind(this));
        }

        this.renderMoreResultsLock = false;
        timer();

        if ( $.fixoversize && models.length > 0 ) {
            console.log("[Reserva] Calling fixoversize update (renderMoreResults)");
            $.fixoversize("update");
        }
    },

    //
    // User actions
    //

    /**
     * Sortier-Reihenfolge setzen
     *
     * @param order {String} Neue Sortier-Reihenfolge (auto, stars, ratings, price)
     */
    setSortOrder: function(order) {
        var previous = this.get("sortBy");
        if ( order == previous ) return;
        var sortArrayIndex = {
            'auto': 0,
            'stars': 1,
            'ratings': 2,
            'price': 3
        }[order];

        this.set({
            "sortBy": order,
            "sortArrayIndex": sortArrayIndex,
        });
        this.resultStore.comparator = function(result) {
            return result.attributes.sort[sortArrayIndex];
        };

        this.sortResults();
        this.renderResults();
    },

    /**
     * Ergebnisse neu sortieren
     */
    sortResults: function() {
        var timer = MiniTimer("SortResults: " + this.get("sortBy"));
        var sortArrayIndex = this.get("sortArrayIndex");

        /* Ergebnisse sortieren */
        this.resultStore.sort();

        /* Auch noch alle Items jeweils einzeln sortieren */
        this.resultStore.each(function(result) {
            result.set("classes", _.sortBy(result.get("classes"), function(item) {
                return item.sort[3];
            }));

            /* Zusätzlich müssen wir die Ergebnisse neu rendern, da sich die Reihenfolge der Kategorien geändert hat */
            var view = this.resultViews[result.id];
            if ( view.rendered ) {
                this.resultViews[result.id].render();
            }
        }.bind(this));
        timer();
    },

    toggleTag: function(tag_id) {
        var tags = this.get("selectedTags");
        tag_id = parseInt(tag_id, 10);
        if ( _.contains(tags, tag_id) ) {
            tags.splice(tags.indexOf(tag_id), 1);
        } else {
            tags.push(tag_id);
        }

        this.set("selectedTags", tags);
        // Backbone wont trigger on set cause the array is still the same array object
        this.trigger("change:selectedTags", this);
    },

    //
    // View Methods
    //

    scrollHandler: function() {
        if ( this.get("tab") !== "list" ) return;
        var scrollTop = $(window).scrollTop();
        var boxes = this.$list.find('.result-box');
        if ( !boxes.length ) {
            // Nothing has been rendered yet
            return;
        }

        var lastBoxOffset = boxes.last().offset().top;
        var viewportBottom = scrollTop + $(window).height() + 100;
        if ( viewportBottom > lastBoxOffset && !this.renderMoreResultsLock ) {
            // Mehr Ergebnisse rendern
            setTimeout(this.renderMoreResults, 0);
        }

        this.viewAnalyticsThrottled();
    },

    viewAnalytics: function() {
        var scrollTop = $(window).scrollTop();
        var viewportHeight = $(window).height();
        var boxHeight;
        this.resultStore.getVisible().each(function(result) {
            /* Schon gesendet? */
            if ( result.viewSent ) return;

            /* Gibt es das View überhaupt? */
            var view = this.resultViews[result.id];
            if ( !view || !view.rendered ) return;

            /* Ist die Box momentan sichtbar? */
            var offset = view.$box.offset();
            if ( !boxHeight ) {
                boxHeight = view.$box.outerHeight(true);
            }

            /* Wenn die Box komplett auf dem Bildschirm sichtbar ist, zählen wir den View */
            if ( offset && offset.top <= (scrollTop + viewportHeight - boxHeight) ) {
                console.log("[ReservaApp] viewAnalytics: view " + result.get("id"));
                window.TraminoAnalytics.send(result.get("id"), "view");
                result.viewSent = true;
            }
        }.bind(this));
    },

    scrollUp: function() {
        var currentScrollTop = $('html').scrollTop();
        if ( currentScrollTop === 0 ) return; /* Nicht scrollen wenn ganz oben */

        $('html, body').animate({
            scrollTop: $('.tabs').offset().top - 20,
        }, 400);
    },
});

module.exports = ReservaApp;



/** WEBPACK FOOTER **
 ** /Users/mitch/jsapps/modules/reserva/engine/main.js
 **/